function skipWs(text, i) {
    while (i < text.length && /\s/.test(text[i]))
        i++;
    return i;
}

function readString(text, i) {
    if (text[i] !== '"')
        throw new SyntaxError(`Expected string at ${i}`);
    i++;
    while (i < text.length) {
        if (text[i] === '\\') {
            i += 2;
            continue;
        }
        if (text[i] === '"')
            return i + 1;
        i++;
    }
    throw new SyntaxError('Unterminated string');
}

function readStringValue(text, start, end) {
    return JSON.parse(text.slice(start, end));
}

function readNumber(text, i) {
    if (text[i] === '-')
        i++;
    while (i < text.length && /[0-9eE+.\-]/.test(text[i]))
        i++;
    return i;
}

function readLiteral(text, i) {
    if (text.startsWith('true', i))
        return i + 4;
    if (text.startsWith('false', i))
        return i + 5;
    if (text.startsWith('null', i))
        return i + 4;
    throw new SyntaxError(`Invalid literal at ${i}`);
}

function recordSpan(spans, start, end, path) {
    spans.push({ start, end, path: path.slice() });
}

function parseValue(text, i, path, spans) {
    i = skipWs(text, i);
    const start = i;
    const ch = text[i];

    if (ch === '{')
        i = parseObject(text, i, path, spans);
    else if (ch === '[')
        i = parseArray(text, i, path, spans);
    else if (ch === '"')
        i = readString(text, i);
    else if (ch === 't' || ch === 'f' || ch === 'n')
        i = readLiteral(text, i);
    else if (ch === '-' || (ch >= '0' && ch <= '9'))
        i = readNumber(text, i);
    else
        throw new SyntaxError(`Unexpected token at ${i}`);

    recordSpan(spans, start, i, path);
    return i;
}

function parseObject(text, i, path, spans) {
    i++; // {
    i = skipWs(text, i);
    if (text[i] === '}')
        return i + 1;

    while (true) {
        i = skipWs(text, i);
        const keyStart = i;
        i = readString(text, i);
        const key = readStringValue(text, keyStart, i);
        i = skipWs(text, i);
        if (text[i] !== ':')
            throw new SyntaxError(`Expected ':' at ${i}`);
        i++;
        i = parseValue(text, i, path.concat(key), spans);
        i = skipWs(text, i);
        if (text[i] === '}')
            return i + 1;
        if (text[i] === ',') {
            i++;
            continue;
        }
        throw new SyntaxError(`Expected ',' or '}' at ${i}`);
    }
}

function parseArray(text, i, path, spans) {
    i++; // [
    i = skipWs(text, i);
    if (text[i] === ']')
        return i + 1;

    let index = 0;
    while (true) {
        i = parseValue(text, i, path.concat(index), spans);
        index++;
        i = skipWs(text, i);
        if (text[i] === ']')
            return i + 1;
        if (text[i] === ',') {
            i++;
            continue;
        }
        throw new SyntaxError(`Expected ',' or ']' at ${i}`);
    }
}

/**
 * Collect syntactic spans for every JSON value in `text`.
 * @param {string} text
 * @returns {Array<{ start: number, end: number, path: Array<string|number> }>}
 */
export function collectJsonSpans(text) {
    if (!text)
        return [];

    const spans = [];
    let i = skipWs(text, 0);
    if (i >= text.length)
        return spans;

    try {
        if (text[i] === '{')
            parseObject(text, i, [], spans);
        else if (text[i] === '[')
            parseArray(text, i, [], spans);
        else
            parseValue(text, i, [], spans);
    }
    catch {
        return [];
    }

    return spans;
}

function pathsEqual(a, b) {
    if (!a || !b || a.length !== b.length)
        return false;
    for (let i = 0; i < a.length; i++) {
        if (a[i] !== b[i])
            return false;
    }
    return true;
}

/**
 * Returns the start offset of the JSON value at `pathSegments`, or null.
 * @param {string} text
 * @param {Array<string|number>} pathSegments
 * @returns {number|null}
 */
export function jsonOffsetAtPath(text, pathSegments) {
    if (!text || !pathSegments?.length)
        return null;

    const spans = collectJsonSpans(text);
    for (let s = 0; s < spans.length; s++) {
        if (pathsEqual(spans[s].path, pathSegments))
            return spans[s].start;
    }
    return null;
}

function escapeRegExp(value) {
    return String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function findOffsetByObjectIdInSpans(text, objectId) {
    if (!text || objectId == null || objectId === '')
        return null;

    let parsed;
    try {
        parsed = JSON.parse(text);
    }
    catch {
        return findOffsetByObjectIdInText(text, objectId);
    }

    const spans = collectJsonSpans(text);
    for (let s = 0; s < spans.length; s++) {
        const span = spans[s];
        const node = resolveParsedNode(parsed, span.path);
        if (getObjectId(node) == objectId)
            return span.start;
    }

    return findOffsetByObjectIdInText(text, objectId);
}

function findOffsetByObjectIdInText(text, objectId) {
    const re = new RegExp(`"\\$objectId"\\s*:\\s*"${escapeRegExp(objectId)}"`, 'i');
    const match = re.exec(text);
    return match ? match.index : null;
}

/**
 * Returns the path segments to the innermost JSON value whose syntactic span contains `offset`.
 * @param {string} text
 * @param {number} offset
 * @returns {Array<string|number>|null}
 */
export function jsonPointerAtOffset(text, offset) {
    if (!text || offset < 0 || offset > text.length)
        return null;

    const spans = collectJsonSpans(text);
    if (!spans.length) {
        let i = skipWs(text, 0);
        if (i >= text.length)
            return null;
        try {
            if (text[i] === '{')
                parseObject(text, i, [], spans);
            else if (text[i] === '[')
                parseArray(text, i, [], spans);
            else
                parseValue(text, i, [], spans);
        }
        catch {
            return null;
        }
    }

    let best = null;
    for (let s = 0; s < spans.length; s++) {
        const span = spans[s];
        if (offset >= span.start && offset < span.end) {
            if (!best || span.path.length >= best.path.length)
                best = span;
        }
    }

    return best ? best.path : null;
}

export function resolveParsedNode(parsed, path) {
    let cur = parsed;
    for (let i = 0; i < path.length; i++)
        cur = cur[path[i]];
    return cur;
}

export function getObjectId(obj) {
    if (!obj || typeof obj !== 'object' || Array.isArray(obj))
        return null;

    for (const key of Object.keys(obj)) {
        if (key.toLowerCase() === '$objectid')
            return obj[key];
    }
    return null;
}

export function findObjectIdAncestor(parsed, path) {
    for (let len = path.length; len >= 0; len--) {
        const node = len === 0 ? parsed : resolveParsedNode(parsed, path.slice(0, len));
        const id = getObjectId(node);
        if (id != null && id !== '')
            return id;
    }
    return null;
}

function isTypedTreeNode(node) {
    return node
        && typeof node === 'object'
        && !Array.isArray(node)
        && '$typeSchema' in node;
}

/** Walk up from cursor path to the deepest object node. */
export function normalizePathToObject(parsed, path) {
    let p = path.slice();
    while (p.length) {
        const node = resolveParsedNode(parsed, p);
        if (node !== null && typeof node === 'object')
            return p;
        p = p.slice(0, -1);
    }
    return [];
}

/**
 * Resolve $objectId for identity matching without skipping past a typed child
 * (e.g. an Action without $objectId) to a parent layout that has one.
 */
export function findObjectIdForResolution(parsed, path) {
    for (let len = path.length; len >= 0; len--) {
        const node = len === 0 ? parsed : resolveParsedNode(parsed, path.slice(0, len));
        if (!node || typeof node !== 'object' || Array.isArray(node))
            continue;

        const id = getObjectId(node);
        if (id != null && id !== '')
            return id;

        if (isTypedTreeNode(node))
            break;
    }
    return null;
}

export function findNodeByObjectId(root, id) {
    if (root == null || id == null)
        return null;

    if (typeof root === 'object' && !Array.isArray(root)) {
        const oid = getObjectId(root);
        if (oid == id)
            return root;
    }

    if (Array.isArray(root)) {
        for (let i = 0; i < root.length; i++) {
            const found = findNodeByObjectId(root[i], id);
            if (found)
                return found;
        }
        return null;
    }

    if (typeof root === 'object') {
        const keys = Object.keys(root);
        for (let j = 0; j < keys.length; j++) {
            const child = root[keys[j]];
            if (child != null && typeof child === 'object') {
                const found = findNodeByObjectId(child, id);
                if (found)
                    return found;
            }
        }
    }

    return null;
}

function resolvePathInModel(model, path) {
    if (!path.length)
        return model;

    let cur = model;
    for (let i = 0; i < path.length; i++) {
        if (cur == null || typeof cur !== 'object')
            return null;
        cur = cur[path[i]];
    }
    return cur;
}

/**
 * Map a cursor offset in JSON text to the corresponding node in the live model.
 * @param {string} jsonText
 * @param {number} offset
 * @param {object} model
 * @returns {object|null}
 */
export function resolveJsonCursorToModelNode(jsonText, offset, model) {
    const parsed = JSON.parse(jsonText);
    let path = jsonPointerAtOffset(jsonText, offset);

    if (!path || !path.length)
        return model;

    path = normalizePathToObject(parsed, path);
    if (!path.length)
        return model;

    const byPath = resolvePathInModel(model, path);
    if (byPath != null && typeof byPath === 'object')
        return byPath;

    const objectId = findObjectIdForResolution(parsed, path);
    if (objectId != null) {
        const byId = findNodeByObjectId(model, objectId);
        if (byId)
            return byId;
    }

    return null;
}

function findModelPathSegments(model, node, path = []) {
    if (model == node)
        return path;

    if (path.length > 60)
        return null;

    if (Array.isArray(model)) {
        for (let i = 0; i < model.length; i++) {
            const item = model[i];
            if (typeof item === 'object' && !Array.isArray(item)) {
                path.push(i);
                const found = findModelPathSegments(item, node, path);
                if (found)
                    return found;
                path.pop();
            }
        }
        return null;
    }

    if (model && typeof model === 'object') {
        for (const key in model) {
            if (key.substr(0, 1) === '$' || key === 'anyOf' || key === 'allOf' || key === 'oneOf' || key === 'elm')
                continue;
            if (key === '$schema')
                return null;

            const item = model[key];
            if (typeof item === 'object') {
                path.push(key);
                const found = findModelPathSegments(item, node, path);
                if (found)
                    return found;
                path.pop();
            }
        }
    }

    return null;
}

/**
 * Map a model tree node to the corresponding cursor offset in JSON text.
 * @param {string} jsonText
 * @param {object} node
 * @param {object} model
 * @returns {number|null}
 */
export function resolveModelNodeToJsonOffset(jsonText, node, model) {
    if (!node || !jsonText || !model)
        return null;

    if (node === model) {
        const spans = collectJsonSpans(jsonText);
        return spans.length ? spans[0].start : 0;
    }

    const segments = findModelPathSegments(model, node);
    if (segments?.length) {
        const byPath = jsonOffsetAtPath(jsonText, segments);
        if (byPath != null)
            return byPath;
    }

    const objectId = getObjectId(node);
    if (objectId != null && objectId !== '')
        return findOffsetByObjectIdInSpans(jsonText, objectId);

    return null;
}
